import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { db, storage } from "../../Firebase/Firebase.utils";
import { revalidateFor } from "./revalidatePublic";

/**
 * Photo data model — matches ravenv20 for cross-app compatibility:
 *
 *   Storage:   Photos/Listings/<listingId>/<timestamp>_<index>_<filename>
 *   Firestore: photos/<listingId>_<timestamp>_<index> = {
 *                listingId, url, primary, comments[], createdAt,
 *                uploadedBy: { uid, email, displayName, roles }
 *              }
 *
 * Both apps WRITE to both Storage and Firestore. ravenv20 READS from the
 * `photos` Firestore collection (where listingId == id) — so the CMS
 * does the same to make sure the same photo set is visible everywhere.
 * The public site (nextjsprojv1) still reads from Storage directly via
 * listAll, so any upload from either app shows up on the website too.
 *
 * `primary` field on the photo doc is vestigial — primary is actually
 * tracked by `primaryPhotoUrl` on the listing doc. We initialize it
 * to false on upload for compatibility but never read it.
 */
const folderRef = (listingId) => ref(storage, `Photos/Listings/${listingId}`);

function toMillis(createdAt) {
  if (!createdAt) return 0;
  if (typeof createdAt.toDate === "function") return createdAt.toDate().getTime();
  if (typeof createdAt.seconds === "number") return createdAt.seconds * 1000;
  const d = new Date(createdAt);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Fetch every photo doc for a listing from the `photos` Firestore
 * collection. Sorted newest first by createdAt.
 */
export async function fetchListingPhotos(listingId) {
  const q = query(
    collection(db, "photos"),
    where("listingId", "==", listingId),
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  docs.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  return docs;
}

/**
 * Upload one file. Writes the file to Storage AND creates a `photos`
 * Firestore doc with metadata. Mirrors ravenv20's upload schema so a
 * photo uploaded from either app appears in both.
 *
 * Returns the new photo doc (with id, url, uploadedBy, etc.).
 */
export async function uploadListingPhoto({
  listingId,
  file,
  currentUser,
  onProgress,
  index = 0,
}) {
  const timestamp = Date.now();
  const fileName = `${timestamp}_${index}_${file.name}`;
  const storagePath = `Photos/Listings/${listingId}/${fileName}`;
  const fileRef = ref(storage, storagePath);
  const task = uploadBytesResumable(fileRef, file);

  const url = await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => onProgress?.(snap.bytesTransferred / snap.totalBytes),
      reject,
      async () => {
        try {
          resolve(await getDownloadURL(task.snapshot.ref));
        } catch (err) {
          reject(err);
        }
      },
    );
  });

  const photoId = `${listingId}_${timestamp}_${index}`;
  const photoData = {
    listingId,
    url,
    primary: false,
    comments: [],
    createdAt: new Date(),
    uploadedBy: {
      uid: currentUser?.uid || null,
      email: currentUser?.email || null,
      displayName:
        currentUser?.displayName || currentUser?.email || null,
      roles: currentUser?.roles || [],
    },
  };
  await setDoc(doc(db, "photos", photoId), photoData);
  // The for-sale detail page caches its gallery server-side — refresh it.
  revalidateFor("listings");

  return { id: photoId, ...photoData };
}

/**
 * Delete a photo. Removes the Firestore doc + the Storage original.
 * Also best-effort-cleans up any auto-generated webp variants left
 * behind by the resize-images extension (something ravenv20's
 * delete flow does NOT do — variants become orphans there).
 */
export async function deleteListingPhoto({ photo, listingId }) {
  // 1. Delete the Firestore photos doc first. If this fails we abort
  //    rather than leaving an orphaned Firestore record pointing at a
  //    deleted Storage object.
  await deleteDoc(doc(db, "photos", photo.id));

  // 2. Parse the storage path from the URL to identify the base filename.
  let baseName = null;
  try {
    const u = new URL(photo.url);
    const encodedPath = u.pathname.split("/o/").pop() || "";
    const filename = decodeURIComponent(encodedPath)
      .split("/")
      .pop()
      .split("?")[0];
    const m = filename.match(/^(.+?)_\d+x\d+\.webp$/i);
    baseName = m ? m[1] : filename.replace(/\.[^.]+$/, "");
  } catch {
    /* swallow — we'll best-effort below */
  }

  // 3. Find every Storage object sharing that base name (original +
  //    auto-generated webp variants) and delete them.
  if (baseName) {
    try {
      const list = await listAll(folderRef(listingId));
      const matching = list.items.filter((item) => {
        const fn = item.name.split("/").pop();
        const m = fn.match(/^(.+?)_\d+x\d+\.webp$/i);
        const base = m ? m[1] : fn.replace(/\.[^.]+$/, "");
        return base === baseName;
      });
      await Promise.all(
        matching.map((item) =>
          deleteObject(item).catch((err) => {
            console.warn(
              "[deleteListingPhoto] variant cleanup failed:",
              item.fullPath,
              err,
            );
          }),
        ),
      );
    } catch (err) {
      console.warn("[deleteListingPhoto] listAll failed:", err);
    }
  }
  // After Storage cleanup, since the public gallery reads both sources.
  revalidateFor("listings");
}
